import Box from '@mui/material/Box';
import BarChartIcon from '@mui/icons-material/BarChart';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinkIcon from '@mui/icons-material/Link';
import SearchIcon from '@mui/icons-material/Search';
import SecurityIcon from '@mui/icons-material/Security';
import type { FC } from 'react';
import { getSecurityNavItemPath } from 'src/mindtime/config/securityNav';
import type {
  WazuhThreatHuntingAgentSummaryData,
  WazuhThreatHuntingEndpointItem,
  WazuhThreatHuntingFindingItem,
  WazuhThreatHuntingQuery,
  WazuhThreatHuntingSummaryData,
} from 'src/types/mindtime/wazuhThreatHunting';
import type { Column } from 'src/types/table';
import {
  buildDetectionDetailsRows,
  buildDetectionEvidenceRows,
  collectMitreIds,
  EDR_COLORS,
  EdrDateTime,
  EdrDetailEndpointSidebar,
  EdrDetailKeyValueRows,
  EdrDetailSectionCard,
  EdrExpandableText,
  EdrKpiCard,
  EdrMitreDocsLinks,
  EdrSeverityPill,
  EdrStatusPill,
  EdrTruncatedText,
  edrMonoStyle,
  edrMutedStyle,
  formatEdrDateTime,
  formatEndpointLabel,
  edrEndpointNameLinkStyle,
  getWindowsEventMessage,
  type EdrEntityDetailPageProps,
  type EdrSeverityLevel,
} from '../shared/edrPrimitives';

import { toEdrSeverityLevel, EDR_SEVERITY_FILTER_OPTIONS } from '../shared/edrFormat';

export const threatHuntingSeverityToLevel = toEdrSeverityLevel;
export const THREAT_HUNTING_SEVERITY_FILTER_OPTIONS = EDR_SEVERITY_FILTER_OPTIONS;

const normalizeKey = (value: string | undefined | null): string =>
  (value ?? '').toString().trim().toLowerCase();

export const toThreatHuntingTitle = (value: string | undefined | null): string => {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};


/** Id for GET /threat-hunting/findings/{id} — prefers `id`, then `_id`. */
export const getThreatHuntingFindingId = (
  item: Pick<WazuhThreatHuntingFindingItem, 'id' | '_id'> | null | undefined
): string => String(item?.id ?? item?._id ?? '').trim();

export type ThreatHuntingFacets = {
  mitreIds: string[];
  eventTypes: string[];
};

export const emptyThreatHuntingFacets = (): ThreatHuntingFacets => ({
  mitreIds: [],
  eventTypes: [],
});

export const mapThreatHuntingFacetOptions = (values: string[]) =>
  values.map((value) => ({ label: value, value }));

export const buildThreatHuntingAgentSummaryFromFindings = (
  systemWazuhAgentId: string,
  items: WazuhThreatHuntingFindingItem[]
): WazuhThreatHuntingAgentSummaryData => {
  const matchesBySeverity = { critical: 0, high: 0, medium: 0, low: 0 };

  items.forEach((item) => {
    const level = threatHuntingSeverityToLevel(item.severity);
    if (level in matchesBySeverity) matchesBySeverity[level] += 1;
  });

  return {
    systemWazuhAgentId,
    threatMatches: items.length,
    matchesBySeverity,
  };
};

export const mergeThreatHuntingFacets = (
  items: WazuhThreatHuntingFindingItem[],
  previous: ThreatHuntingFacets
): ThreatHuntingFacets => {
  const mitreIds = new Set(previous.mitreIds);
  const eventTypes = new Set(previous.eventTypes);

  items.forEach((item) => {
    if (item.mitreId) mitreIds.add(item.mitreId);
    (item.mitreIds ?? []).forEach((id) => {
      if (id) mitreIds.add(id);
    });
    if (item.eventType) eventTypes.add(item.eventType);
  });

  return {
    mitreIds: Array.from(mitreIds).sort((a, b) => a.localeCompare(b)),
    eventTypes: Array.from(eventTypes).sort((a, b) => a.localeCompare(b)),
  };
};


export type ThreatHuntingAgentFilters = {
  user?: string;
  mitreId?: string;
  eventType?: string;
  severityBand?: string;
};

export const buildThreatHuntingAgentFindingsQuery = (
  systemWazuhAgentId: string,
  page: number,
  pageSize: number,
  search: string,
  filters: ThreatHuntingAgentFilters = {}
): WazuhThreatHuntingQuery => ({
  systemWazuhAgentId,
  page,
  pageSize,
  ...(search.trim() ? { search: search.trim() } : {}),
  ...(filters.user ? { user: filters.user } : {}),
  ...(filters.mitreId ? { mitreId: filters.mitreId } : {}),
  ...(filters.eventType ? { eventType: filters.eventType } : {}),
  ...(filters.severityBand ? { severityBand: filters.severityBand } : {}),
});

const formatThreatHuntingKpiNumber = (value: number | undefined | null): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return value.toLocaleString();
};

export const ThreatSummaryKpiGrid: FC<{ summary: WazuhThreatHuntingSummaryData | null }> = ({
  summary,
}) => (
  <Box display="grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16 }}>
    <EdrKpiCard
      label="Threat Matches"
      value={formatThreatHuntingKpiNumber(summary?.threatMatches)}
      icon={<GpsFixedIcon style={{ fontSize: 18 }} />}
      iconColor={EDR_COLORS.brand500}
    />
    <EdrKpiCard
      label="Threat Matches by Severity"
      value={(
        <Box display="flex" flexWrap="wrap" style={{ gap: 6, fontSize: 11, fontWeight: 500 }}>
          <EdrStatusPill
            tone="critical"
            label={`${formatThreatHuntingKpiNumber(summary?.matchesBySeverity?.critical)} Critical`}
            dot={false}
          />
          <EdrStatusPill
            tone="high"
            label={`${formatThreatHuntingKpiNumber(summary?.matchesBySeverity?.high)} High`}
            dot={false}
          />
          <EdrStatusPill
            tone="medium"
            label={`${formatThreatHuntingKpiNumber(summary?.matchesBySeverity?.medium)} Medium`}
            dot={false}
          />
          <EdrStatusPill
            tone="success"
            label={`${formatThreatHuntingKpiNumber(summary?.matchesBySeverity?.low)} Low`}
            dot={false}
          />
        </Box>
      )}
      icon={<BarChartIcon style={{ fontSize: 18 }} />}
      iconColor={EDR_COLORS.critical}
    />
  </Box>
);

type ThreatHuntingListColumnScope = 'global' | 'endpoint';

export const getThreatHuntingFindingListColumns = (
  scope: ThreatHuntingListColumnScope
): Column[] => {
  const isEndpoint = scope === 'endpoint';

  return [
    ...(isEndpoint
      ? []
      : [
          {
            id: 'endpoint',
            label: 'Endpoint',
            render: (row) => (
              <EdrTruncatedText
                value={(row as unknown as WazuhThreatHuntingFindingItem).endpoint || '—'}
                style={edrMonoStyle}
                maxWidth={160}
              />
            ),
          },
          {
            id: 'hostName',
            label: 'Endpoint Label',
            render: (row) => (
              <EdrTruncatedText
                value={formatEndpointLabel((row as unknown as WazuhThreatHuntingFindingItem).hostName)}
                style={edrMutedStyle}
                maxWidth={160}
              />
            ),
          },
          {
            id: 'organization',
            label: 'Organization',
            render: (row) => {
              const item = row as unknown as WazuhThreatHuntingFindingItem;
              return (
                <EdrTruncatedText
                  value={item.organizationName || item.organization || '—'}
                  style={edrMutedStyle}
                  maxWidth={160}
                />
              );
            },
          },
        ]),
    {
      id: 'detection',
      label: 'Detection',
      width: isEndpoint ? '38%' : '32%',
      render: (row) => (
        <EdrTruncatedText
          value={(row as unknown as WazuhThreatHuntingFindingItem).detection || '—'}
          style={{ fontWeight: 600, color: EDR_COLORS.textPrimary, width: '100%' }}
          maxWidth="100%"
        />
      ),
    },
    {
      id: 'mitreId',
      label: 'MITRE ATT&CK',
      width: isEndpoint ? '14%' : undefined,
      render: (row) => (
        <span style={{ ...edrMonoStyle, color: EDR_COLORS.brand600, fontWeight: 600 }}>
          {(row as unknown as WazuhThreatHuntingFindingItem).mitreId ||
            (row as unknown as WazuhThreatHuntingFindingItem).mitreIds?.[0] ||
            '—'}
        </span>
      ),
    },
    {
      id: 'user',
      label: 'User',
      width: isEndpoint ? '12%' : undefined,
      render: (row) => (
        <span style={edrMonoStyle}>
          {(row as unknown as WazuhThreatHuntingFindingItem).user || '—'}
        </span>
      ),
    },
    {
      id: 'severity',
      label: 'Severity',
      width: isEndpoint ? '12%' : undefined,
      render: (row) => {
        const item = row as unknown as WazuhThreatHuntingFindingItem;
        return (
          <EdrSeverityPill
            level={threatHuntingSeverityToLevel(item.severity)}
            label={toThreatHuntingTitle(item.severity) || '—'}
          />
        );
      },
    },
    {
      id: 'timestamp',
      label: 'Timestamp',
      width: isEndpoint ? '24%' : undefined,
      render: (row) => (
        <EdrDateTime value={(row as unknown as WazuhThreatHuntingFindingItem).timestamp} />
      ),
    },
  ];
};

const formatEndpointCount = (value: number | undefined | null): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return value.toLocaleString();
};

const severityCountCell = (value: number | undefined | null, criticalTint = false) => {
  const count = typeof value === 'number' && !Number.isNaN(value) ? value : 0;
  return (
    <span
      style={{
        fontWeight: 600,
        color: criticalTint && count > 0 ? EDR_COLORS.critical : EDR_COLORS.textPrimary,
      }}
    >
      {formatEndpointCount(count)}
    </span>
  );
};

export const getThreatHuntingAffectedEndpointListColumns = (): Column[] => [
  {
    id: 'systemWazuhAgentId',
    label: 'Endpoint',
    sortable: true,
    render: (row) => {
      const item = row as unknown as WazuhThreatHuntingEndpointItem;
      const canOpen = Boolean(item.systemWazuhAgentId?.trim());
      return (
        <span style={edrEndpointNameLinkStyle(canOpen)}>
          {item.endpoint ?? item.name ?? item.systemWazuhAgentId ?? '—'}
        </span>
      );
    },
  },
  {
    id: 'hostName',
    label: 'Endpoint Label',
    render: (row) => (
      <span style={edrMutedStyle}>
        {formatEndpointLabel((row as unknown as WazuhThreatHuntingEndpointItem).hostName)}
      </span>
    ),
  },
  {
    id: 'organization',
    label: 'Organization',
    render: (row) => {
      const item = row as unknown as WazuhThreatHuntingEndpointItem;
      return (
        <span style={edrMutedStyle}>
          {item.organization ?? item.organizationName ?? '—'}
        </span>
      );
    },
  },
  {
    id: 'threatMatches',
    label: 'Threat Matches',
    align: 'center',
    sortable: true,
    render: (row) => {
      const item = row as unknown as WazuhThreatHuntingEndpointItem;
      return (
        <span style={{ fontWeight: 700, color: EDR_COLORS.textPrimary }}>
          {formatEndpointCount(item.threatMatches)}
        </span>
      );
    },
  },
  {
    id: 'critical',
    label: 'Critical',
    align: 'center',
    sortable: true,
    render: (row) =>
      severityCountCell(
        (row as unknown as WazuhThreatHuntingEndpointItem).matchesBySeverity?.critical,
        true
      ),
  },
  {
    id: 'high',
    label: 'High',
    align: 'center',
    sortable: true,
    render: (row) =>
      severityCountCell((row as unknown as WazuhThreatHuntingEndpointItem).matchesBySeverity?.high),
  },
  {
    id: 'medium',
    label: 'Medium',
    align: 'center',
    sortable: true,
    render: (row) =>
      severityCountCell((row as unknown as WazuhThreatHuntingEndpointItem).matchesBySeverity?.medium),
  },
  {
    id: 'low',
    label: 'Low',
    align: 'center',
    sortable: true,
    render: (row) =>
      severityCountCell((row as unknown as WazuhThreatHuntingEndpointItem).matchesBySeverity?.low),
  },
];

export const buildThreatHuntingDetailProps = (
  item: WazuhThreatHuntingFindingItem
): Omit<EdrEntityDetailPageProps, 'loading' | 'notFoundMessage'> => {
  const severityLevel = threatHuntingSeverityToLevel(item.severity);
  const severityLabel = toThreatHuntingTitle(item.severity) || '—';
  const mitreIds = collectMitreIds(item.mitreId, item.mitreIds);
  const organization = item.organizationName?.trim() || item.organization?.trim() || '—';
  const tactics = item.tactics?.map((t) => t?.trim()).filter(Boolean) ?? [];
  const techniqueLabel = item.techniqueNames?.[0]?.trim() || mitreIds[0] || '—';
  const description = item.detection?.trim() || '';
  const message = getWindowsEventMessage(item.data);
  const evidenceRows = buildDetectionEvidenceRows(item);
  const detectionId = getThreatHuntingFindingId(item);

  return {
    backLink: {
      label: 'Back to Threat Hunting',
      to: getSecurityNavItemPath('threat-hunting') ?? '',
    },
    title: mitreIds[0] || description || 'Finding',
    badges: <EdrSeverityPill level={severityLevel} label={severityLabel} />,
    metaLine: (
      <>
        {techniqueLabel}
        {' · Detected '}
        {formatEdrDateTime(item.timestamp)}
        {' on '}
        <strong style={{ color: EDR_COLORS.textPrimary }}>{item.endpoint?.trim() || '—'}</strong>
      </>
    ),
    main: (
      <>
        {description || message ? (
          <EdrDetailSectionCard
            icon={<InfoOutlinedIcon style={{ fontSize: 16 }} />}
            title="Description"
          >
            {description ? <EdrExpandableText text={description} lines={3} /> : null}
            {message ? (
              <Box style={{ marginTop: description ? 12 : 0 }}>
                <EdrExpandableText text={message} lines={4} />
              </Box>
            ) : null}
          </EdrDetailSectionCard>
        ) : null}

        <EdrDetailSectionCard icon={<SecurityIcon style={{ fontSize: 16 }} />} title="Technique">
          <EdrDetailKeyValueRows
            rows={[
              { label: 'Technique', value: techniqueLabel },
              {
                label: 'MITRE ID',
                value: mitreIds.length ? mitreIds.join(', ') : '—',
                mono: true,
              },
              { label: 'Tactic', value: tactics.length ? tactics.join(', ') : '—' },
              {
                label: 'Threat Category',
                value: item.threatCategory?.trim() || item.eventType?.trim() || '—',
              },
              { label: 'Severity', value: severityLabel },
              { label: 'Detection Time', value: <EdrDateTime value={item.timestamp} /> },
            ]}
          />
        </EdrDetailSectionCard>

        {evidenceRows.length > 0 ? (
          <EdrDetailSectionCard
            icon={<SearchIcon style={{ fontSize: 16 }} />}
            title="Detection Evidence"
          >
            <EdrDetailKeyValueRows rows={evidenceRows} />
          </EdrDetailSectionCard>
        ) : null}

        {mitreIds.length > 0 ? (
          <EdrDetailSectionCard icon={<LinkIcon style={{ fontSize: 16 }} />} title="MITRE References">
            <EdrMitreDocsLinks ids={mitreIds} />
          </EdrDetailSectionCard>
        ) : null}
      </>
    ),
    sidebar: (
      <>
        <EdrDetailEndpointSidebar
          endpoint={item.endpoint?.trim() || '—'}
          hostName={item.hostName}
          organization={organization}
          operatingSystem={item.os?.trim() || undefined}
        />
        <EdrDetailSectionCard
          icon={<InfoOutlinedIcon style={{ fontSize: 16 }} />}
          title="Detection Details"
        >
          <EdrDetailKeyValueRows rows={buildDetectionDetailsRows(item, detectionId)} />
        </EdrDetailSectionCard>
      </>
    ),
  };
};
